import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  const [exchangeRates, setExchangeRates] = useState({});

  // Devises supportées avec leurs informations
  const currencies = {
    EUR: {
      code: 'EUR',
      name: 'Euro',
      symbol: '€',
      flag: '🇪🇺',
      region: 'Europe'
    },
    FCFA: {
      code: 'FCFA',
      name: 'Franc CFA',
      symbol: 'FCFA',
      flag: '🌍',
      region: 'Afrique de l\'Ouest'
    },
    USD: {
      code: 'USD',
      name: 'Dollar américain',
      symbol: '$',
      flag: '🇺🇸',
      region: 'États-Unis'
    },
    GBP: {
      code: 'GBP',
      name: 'Livre sterling',
      symbol: '£',
      flag: '🇬🇧',
      region: 'Royaume-Uni'
    },
    CAD: {
      code: 'CAD',
      name: 'Dollar canadien',
      symbol: 'C$',
      flag: '🇨🇦',
      region: 'Canada'
    },
    XOF: {
      code: 'XOF',
      name: 'Franc CFA (XOF)',
      symbol: 'CFA',
      flag: '🇸🇳',
      region: 'Afrique de l\'Ouest'
    },
    XAF: {
      code: 'XAF',
      name: 'Franc CFA (XAF)',
      symbol: 'FCFA',
      flag: '🇨🇲',
      region: 'Afrique centrale'
    },
    MAD: {
      code: 'MAD',
      name: 'Dirham marocain',
      symbol: 'MAD',
      flag: '🇲🇦',
      region: 'Maroc'
    },
    TND: {
      code: 'TND',
      name: 'Dinar tunisien',
      symbol: 'TND',
      flag: '🇹🇳',
      region: 'Tunisie'
    },
    DZD: {
      code: 'DZD',
      name: 'Dinar algérien',
      symbol: 'DZD',
      flag: '🇩🇿',
      region: 'Algérie'
    }
  };

  // Taux de change simulés (en production, vous utiliseriez une API réelle)
  const defaultExchangeRates = {
    EUR: 1.0,
    FCFA: 655.96, // 1 EUR = 655.96 FCFA
    USD: 1.08,
    GBP: 0.85,
    CAD: 1.47,
    XOF: 655.96,
    XAF: 655.96,
    MAD: 10.85,
    TND: 3.35,
    DZD: 145.50
  };

  // Charger les préférences de devise depuis le localStorage
  useEffect(() => {
    const savedCurrency = localStorage.getItem('selectedCurrency');
    if (savedCurrency && currencies[savedCurrency]) {
      setSelectedCurrency(savedCurrency);
    }
    
    // Simuler le chargement des taux de change
    setExchangeRates(defaultExchangeRates);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Changer de devise
  const changeCurrency = (currencyCode) => {
    if (currencies[currencyCode]) {
      setSelectedCurrency(currencyCode);
      localStorage.setItem('selectedCurrency', currencyCode);
    }
  };

  // Convertir un prix d'une devise à une autre
  const convertPrice = (price, fromCurrency = 'EUR', toCurrency = selectedCurrency) => {
    if (fromCurrency === toCurrency) return price;
    
    const fromRate = exchangeRates[fromCurrency] || 1;
    const toRate = exchangeRates[toCurrency] || 1;
    
    // Convertir d'abord en EUR, puis vers la devise cible
    const priceInEUR = price / fromRate;
    const convertedPrice = priceInEUR * toRate;
    
    return Math.round(convertedPrice * 100) / 100; // Arrondir à 2 décimales
  };

  // Formater un prix avec la devise sélectionnée
  const formatPrice = (price, currency = selectedCurrency) => {
    const currencyInfo = currencies[currency];
    if (!currencyInfo) return `${price} €`;
    
    // Pour les devises africaines, ne pas afficher de décimales
    if (['FCFA', 'XOF', 'XAF', 'MAD', 'TND', 'DZD'].includes(currency)) {
      return `${Math.round(price)} ${currencyInfo.symbol}`;
    }
    
    // Pour les autres devises, afficher 2 décimales
    return `${price.toFixed(2)} ${currencyInfo.symbol}`;
  };

  // Obtenir la devise actuelle
  const getCurrentCurrency = () => {
    return currencies[selectedCurrency];
  };

  // Obtenir toutes les devises
  const getAllCurrencies = () => {
    return Object.values(currencies);
  };

  // Obtenir les devises populaires (pour l'affichage)
  const getPopularCurrencies = () => {
    return ['EUR', 'FCFA', 'USD', 'GBP', 'CAD'].map(code => currencies[code]);
  };

  const value = {
    selectedCurrency,
    currencies,
    exchangeRates,
    changeCurrency,
    convertPrice,
    formatPrice,
    getCurrentCurrency,
    getAllCurrencies,
    getPopularCurrencies
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
